import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { isBrowser } from './w-universal';
import { displayed, hasEmptyElement, hasText, ngErrorStyle, ngstyle } from './lib';
interface NodeObserverOf {
    observer: NodeObserver;
    relation: 'parent' | 'child' | 'self';
}
const elementToNodeCache = new Map<Node, NodeObserver>();
const recordNodeCache = new Map<Node, NodeObserver>();

let nodeCount = 0;
/** wait when component is built but not attached to the DOM */
class ConnectObserver {
    MO?: MutationObserver;
    waitingList = new Map<Node, () => void>();
    execWhenConnected(n: Node, cb: () => void) {
        if (n.isConnected) {
            cb();
        } else {
            this.waitingList.set(n, cb);
        }
    }
    startObserve() {
        this.MO = new MutationObserver((mm: MutationRecord[]) => {
            const active = Array.from(this.waitingList.keys()).filter(n => n.isConnected);
            active.forEach(n => {
                const cb = this.waitingList.get(n);
                if (cb) {
                    cb();
                    this.waitingList.delete(n);
                }
            });
        })
        this.MO.observe(document.body, { childList: true, subtree: true });
    }
}
const connectObserver = isBrowser ? new ConnectObserver() : undefined;
export const execWhenConnected = (n: Node, cb: () => void) => {
    if (isBrowser && connectObserver) {
        if (!connectObserver.MO) { connectObserver.startObserve(); }
        connectObserver.execWhenConnected(n, cb);
    }
}

export class NodeObserver {
    id = Math.ceil(Math.random() * 10000);
    target: Node;
    hidden?: boolean;
    mutation = new Subject<boolean>();
    hideStateChange = new Subject<boolean>();
    childStateChange = new Subject<boolean>();
    children: NodeObserver[] = [];
    subscriptions = new Map<NodeObserver, Subscription>();
    parent?: NodeObserver;
    constructor(n: Node) {
        this.target = n;
        this.hideStateChange.pipe(
            distinctUntilChanged(),
            filter(s => s !== undefined)
        ).subscribe(s => {
            this.hidden = s;
        });
    }

    protected addChild(o: NodeObserver) {
        this.subscriptions.set(o, o.hideStateChange.pipe(distinctUntilChanged()).subscribe(s => {
            this.childStateChange.next(s);
        }));
        if (o.parent && o.parent !== this) {
            // remove from previous parent
            o.parent.removeChild(o);
        }
        o.parent = this;
        this.children.push(o);
    }
    private removeChild(c: NodeObserver) {
        this.children.splice(this.children.indexOf(c), 1);
        this.subscriptions.get(c)?.unsubscribe();
        this.subscriptions.delete(c);
        c.parent = undefined;
    }

    getRecordNodeObserver(node: Node): NodeObserver | undefined {
        if (recordNodeCache.has(node)) {
            return recordNodeCache.get(node);
        }
        let current: Node | null = node;
        while (current) {
            if (elementToNodeCache.has(current)) {
                const obs = elementToNodeCache.get(current)!;
                recordNodeCache.set(node, obs);
                return obs;
            }
            current = current.parentNode;
        }
        return undefined;
    }
    private findNodeObserver(n: Node): NodeObserverOf | undefined {
        if (this.target === n) {
            return { observer: this, relation: 'self' };
        } else if (isDescendentOf(this.target, n)) {
            // this is/should become the parent of n
            const obs = this.children.map(c => c.findNodeObserver(n)).find(o => !!o);
            return obs || { observer: this, relation: 'parent' };
        } else if (isDescendentOf(n, this.target)) {
            // this should be a child of n
            return { observer: this, relation: 'child' };
        } else {
            return undefined; // not related
        }
    }

    /** when called without second parameter will create new object (never returns undefined) */
    getNodeObserver(n: Node): NodeObserver;
    getNodeObserver(n: Node, create: true): NodeObserver | undefined;
    getNodeObserver(n: Node, create: false): NodeObserver;
    getNodeObserver(n: Node, create?: boolean): NodeObserver | undefined;
    getNodeObserver(n: Node, create = true): NodeObserver | undefined {
        let o: NodeObserver | undefined;
        /** find existing observer object */
        const relatedObserver = this.findNodeObserver(n);
        if (relatedObserver?.relation === 'self') {
            // it is a match, node has observer already
            o = relatedObserver.observer;
        }
        if (!o && create) {
            /** check if element is in cache, else create a new one */
            o = elementToNodeCache.get(n) || new NodeObserver(n);
            elementToNodeCache.set(n, o);
            /** each time rebuild the tree to mirror DOM */
            this.rebuildTreeFollowingDOMPreserveData();
        }
        return o;
    }
    cleanUp(): boolean {
        // remove any disconnected observers
        // start with children
        const removable = this.children.filter(c => c.cleanUp());
        removable.forEach((c: NodeObserver) => this.removeChild(c));
        return !this.target.isConnected;
    }
    show(level = 0): string {
        return `\n ${'.'.repeat(level)} [${this.id}]${nodeSig(this.target)} (${this.children.length})` +
            (this.children.length > 0 ? ` [${this.children.map(c => c.show(level + 1)).join(',')}]` : '');
    }
    rebuildTreeFollowingDOMPreserveData(): NodeObserver {
        // Create a map to preserve original node data
        const elementToNodeMap = new Map<Node, NodeObserver>();

        function mapElements(node: NodeObserver): void {
            elementToNodeMap.set(node.target, node);
            elementToNodeCache.set(node.target, node);
            node.children.forEach(mapElements);
        }

        mapElements(this);

        function buildDOMTree(element: Node): NodeObserver {
            const node = (elementToNodeMap.get(element) || elementToNodeCache.get(element))!;
            node.children = [];

            // Find direct DOM children that are in our element set
            const directChildren: HTMLElement[] = [];

            function findIncludedChildren(parent: Node): void {
                for (const child of Array.from((parent as Element).children) as HTMLElement[]) {
                    if (elementToNodeMap.has(child) || elementToNodeCache.has(child)) {
                        directChildren.push(child);
                    } else {
                        findIncludedChildren(child);
                    }
                }
            }

            findIncludedChildren(element);

            // Recursively build children
            directChildren.forEach(child => {
                node.addChild(buildDOMTree(child));
            });

            return node;
        }

        return buildDOMTree(this.target);
    }
}
// node descends from element (direct or indirect child)
export const isDescendentOf = (element: Node, node: Node, trace = false): boolean => {
    const children = Array.from((element as HTMLElement).children);
    const isDesc = !!node && (element === node?.parentElement || children.some(c => isDescendentOf(c, node, trace)));
    return isDesc;
};

// tslint:disable-next-line: variable-name
let _rootNode: NodeObserver; // observing DOM - browser only
// track updates, do not watch attributes if problem
let watchAttributes = true; // by default - watch, can disable
interface TrackChange {
    target: Node;
    attribute: string;
    ts: number; // timestamp
    count: number;
}
const trackMutations: TrackChange[] = [];
const trackAttributes = (mm: MutationRecord[], limitPerSec = 300): boolean => {
    // does not track attributes on navbar or if "no-tracking" attribute is present
    const attributeMutations = mm.filter(m => m.type === 'attributes' && (m.target as any).tagName !== 'WIPO-NAVBAR' && !(m.target as HTMLElement).hasAttribute('no-tracking'));
    // remove if older that 1sec
    let maxPerSec = 0;
    const now = Date.now();
    for (let i = trackMutations.length - 1; i > -1; i--) {
        const t = trackMutations[i];
        // record and remove if older than 1 sec
        if (t.ts < (now - 1000)) {
            trackMutations.splice(i, 1);
            maxPerSec = Math.max(t.count, maxPerSec);
            if (t.count > limitPerSec) {
                console.warn('%cw-angular', ngstyle, `Too many attribute updates: "${t.attribute}" updates (${t.count}/sec)! Disabling attribute tracking. Possibly a getter generates a new value on each iteration. If constant updates are necessary, use attribute "no-tracking".`, t.target);
            }
        }
    }
    // keep track
    attributeMutations.forEach(m => {
        let t = trackMutations.find(tm => tm.target === m.target && tm.attribute === m.attributeName);
        if (!t) {
            t = { target: m.target, attribute: m.attributeName ?? '', ts: Date.now(), count: 0 };
            trackMutations.push(t);
        }
        t.count++;
    })
    // if any got more hits per sec than limit - disable (this might affect the visibility of facets)
    const disable = maxPerSec > limitPerSec;
    return !disable;
};
export const initRootNode = (): void => {
    _rootNode = new NodeObserver(document.body);
    startObserve();
};
enum MUTATION_TYPE {
    ATTRIBUTES = 'attributes',
    CHAR_DATA = 'characterData',
    CHILD_LIST = 'childList'
};
const startObserve = () => {
    const MO = new MutationObserver((mm: MutationRecord[]) => {
        if (watchAttributes && !trackAttributes(mm)) {
            MO.disconnect();
            watchAttributes = false;
            startObserve();
        }
        recordNodeCache.clear();
        const filteredMutations = mm.filter(m =>
            m.target.nodeType !== Node.COMMENT_NODE && (
                m.type !== MUTATION_TYPE.ATTRIBUTES ||
                m.type === MUTATION_TYPE.ATTRIBUTES && (
                    // explicitly exclude navbar attributes
                    (m.target as any).tagName !== 'WIPO-NAVBAR'
                )
            )
        );
        if (filteredMutations.length > 0) {
            // Collect unique targets to avoid redundant calls to getRecordNodeObserver
            const uniqueTargets = new Set(filteredMutations.map(m => m.target));
            const targetObservers = Array.from(uniqueTargets).map(target => _rootNode.getRecordNodeObserver(target)).filter(on => !!on);
            // Ensure unique observers since different targets can map to the same observer
            const uniqueObservers = new Set(targetObservers);
            // console.log('%chw MutationObserver', ngstyle, ` detected mutations: ${filteredMutations.length}, unique targets: ${uniqueTargets.size}, unique observers: ${uniqueObservers.size}`);
            uniqueObservers.forEach(node => {
                node.mutation.next(true);
            });
        }
    });
    // we are only interested in attributes that can change visibility
    const attr = watchAttributes ? { attributeFilter: ['style', 'class'] } : {};
    MO.observe(document.body, { childList: true, subtree: true, characterData: true, ...attr });
}


export const observeNode = (n: Node): NodeObserver => {
    return _rootNode.getNodeObserver(n);
};

export const nodeObserverCleanup = () => {
    // NOOP on server
    if (isBrowser) {
        setTimeout(() => {
            _rootNode.cleanUp();
        });
    }
};
export const nodeObserverInfo = (): string | undefined => {
    if (isBrowser) {
        return _rootNode.show(0);
    } else return undefined;
};
export const nodeSig = (e: Node): string => {
    switch (e.nodeType) {
        case Node.COMMENT_NODE: return '#comment';
        case Node.TEXT_NODE: return `#text:${e.textContent}`;
        case Node.ELEMENT_NODE: return `${(e as HTMLElement).tagName} ${Array.from((e as HTMLElement).classList)}`;
        default: return `?${e.nodeName}${e.nodeType}`;
    }
};
export const isNotEmptyOrHiddenElement = (e: Element, self = false): boolean => {
    // tempted to simply calculate the element height, but that might just by scaling to parent, or have padding without any content
    if (!(e instanceof HTMLElement)) { return false; } else {
        const element = e as HTMLElement;
        // see if we hit already evaluated node
        if (!self) {
            // get if exists!
            const observer = _rootNode.getNodeObserver(e, false);
            // exclude not evaluated facets (undefined)
            if (observer?.hidden !== undefined) {
                return !observer.hidden;
            }
        }
        // else just dig-in down
        const d = self || displayed(element);
        const hasT = d && hasText(element, self); // !textEmpty(element.textContent || ''); // has text
        const hasE = d && hasEmptyElement(element);
        const isPlaceholder = d && element.hasAttribute('w-not-empty');
        return d && // hide if content hidden
            (
                hasT || // has text
                hasE || // standard tag visible without text content
                isPlaceholder || // possibly empty placeholder element (w-not-empty attribute) - show it anyway
                Array.from(element.children).some(c => c.nodeType === Node.ELEMENT_NODE && isNotEmptyOrHiddenElement(c))
            );
    }

};
try {
    initRootNode();
} catch (e) {
    // SSR - do nothing
}